
import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { db, firebaseConfig } from '../firebase'; 
import { 
    collection, query, where, getDocs, doc, updateDoc, deleteField, 
    setDoc, serverTimestamp, addDoc, deleteDoc, onSnapshot 
} from 'firebase/firestore';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut, setPersistence, inMemoryPersistence } from 'firebase/auth';
import { UserProfile } from '../types';
import { Card, Button, Input, Badge } from '../components/ui';
import { COURSES } from '../data/courses';

const ManagerDashboard = () => {
  const { userProfile } = useAuth();
  const [team, setTeam] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [isAddingUser, setIsAddingUser] = useState(false);
  
  // Invite State
  const [addUserError, setAddUserError] = useState(''); 
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteStatus, setInviteStatus] = useState('');
  const [manualInviteLink, setManualInviteLink] = useState('');

  const [managingUser, setManagingUser] = useState<UserProfile | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchTeam = useCallback(async () => {
    if (!userProfile?.schoolId) {
        if(userProfile) setLoading(false); 
        return;
    }
    setError('');
    
    try {
      const q = query(collection(db, 'users'), where('schoolId', '==', userProfile.schoolId));
      const querySnapshot = await getDocs(q);
      const teamData: UserProfile[] = [];
      querySnapshot.forEach((doc) => {
        teamData.push(doc.data() as UserProfile);
      });
      setTeam(teamData);
    } catch (err: any) {
      console.error("Error fetching team:", err);
      if (err.code === 'permission-denied') {
        setError("Permissions Error: Your Firestore Rules are blocking access.");
      } else {
        setError("Failed to load group data. " + err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [userProfile]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const formatDateShort = (dateVal: any) => {
    if (!dateVal) return '';
    try {
        const d = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);
        if (isNaN(d.getTime())) return '';
        
        return d.toLocaleDateString('en-GB', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    } catch (e) { return ''; }
  };

  const handleResetProgress = async (user: UserProfile, courseId: string, courseTitle: string) => {
    if (!confirm(`Are you sure you want to reset ${user.displayName}'s progress for "${courseTitle}"?`)) return;

    try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
            [`progress.${courseId}`]: deleteField()
        });
        
        setManagingUser(prev => {
            if (!prev) return null;
            const newProgress = { ...prev.progress };
            delete newProgress[courseId];
            return { ...prev, progress: newProgress };
        });

        await fetchTeam();
        alert(`Progress reset successfully.`);
    } catch (err: any) {
        alert("Failed to reset progress. " + err.message);
    }
  };

  const handleDeleteUser = async () => {
      if (!userToDelete) return;
      setIsDeleting(true);

      try {
          await deleteDoc(doc(db, 'users', userToDelete.uid));
          setUserToDelete(null);
          await fetchTeam(); 
          alert(`${userToDelete.displayName} has been removed.`);
      } catch (err: any) {
          alert("Failed to remove user: " + err.message);
      } finally {
          setIsDeleting(false);
      }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAddingUser(true);
    setAddUserError('');
    setInviteSuccess('');
    setManualInviteLink('');
    setInviteStatus('Creating user account...');
    
    let secondaryApp: any = null;

    try {
        // 1. Initialize a secondary APP only for AUTH (to avoid logging out the current admin)
        const appName = `Secondary-${Date.now()}`;
        secondaryApp = initializeApp(firebaseConfig, appName);
        const secondaryAuth = getAuth(secondaryApp);
        
        await setPersistence(secondaryAuth, inMemoryPersistence);

        let user;
        const tempPassword = Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-8) + "A1!";

        try {
            const userCredential = await createUserWithEmailAndPassword(secondaryAuth, newUserEmail, tempPassword);
            user = userCredential.user;
        } catch (authErr: any) {
            if (authErr.code === 'auth/email-already-in-use') {
                setAddUserError("This email is already registered.");
                setIsAddingUser(false);
                setInviteStatus('');
                if (secondaryApp) await deleteApp(secondaryApp);
                return;
            } else {
                throw authErr;
            }
        }

        if (!user) throw new Error("Authentication failed.");

        const userData = {
            uid: user.uid,
            email: user.email,
            displayName: newUserName,
            role: 'staff',
            schoolId: userProfile?.schoolId,
            schoolName: userProfile?.schoolName,
            progress: {},
            externalTraining: [],
            createdAt: serverTimestamp()
        };

        // 2. Write to Firestore using the MAIN 'db' instance.
        setInviteStatus('Saving user profile...');
        await setDoc(doc(db, 'users', user.uid), userData);
        
        const setupLink = `${window.location.origin}/#/setup?e=${encodeURIComponent(newUserEmail)}&c=${encodeURIComponent(btoa(tempPassword))}`;
        
        // STORE THE LINK IMMEDIATELY so it's available even if email fails
        setManualInviteLink(setupLink);
        
        const managerName = userProfile?.displayName || "The Group Lead";

        // 3. Send Email using MAIN 'db' instance.
        setInviteStatus('Queuing invitation email...');
        try {
            const mailRef = await addDoc(collection(db, 'mail'), {
                to: [newUserEmail],
                message: {
                    subject: `You've been invited – Dads Matter Hub`,
                    text: `Hello ${newUserName},\n\nYou have been invited to join the Dads Matter Training Hub by ${managerName}.\n\nClick here to setup your account: ${setupLink}`,
                    html: `
                        <div style="font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #f8fafc; padding: 20px;">
                            <div style="background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.05);">
                                <div style="background-color: #4f46e5; padding: 30px; text-align: center;">
                                    <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: bold;">Dads Matter Training Hub</h1>
                                </div>

                                <div style="padding: 40px;">
                                    <h2 style="color: #1e293b; margin-top: 0; font-size: 20px;">You've been invited.</h2>
                                    
                                    <p style="color: #475569; font-size: 16px; line-height: 1.6;">Hello <strong>${newUserName}</strong>,</p>
                                    <p style="color: #475569; font-size: 16px; line-height: 1.6;"><strong>${managerName}</strong> has invited you to join the group <strong>${userProfile?.schoolName}</strong> on the Dads Matter Training Hub.</p>
                                    
                                    <p style="color: #475569; font-size: 16px; line-height: 1.6;">This platform gives you access to specialized training on The Children Act, writing court statements, and safeguarding, designed specifically to help you navigate the family court system.</p>

                                    <div style="text-align: center; margin: 40px 0;">
                                        <a href="${setupLink}" style="background-color: #4f46e5; color: #ffffff; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; display: inline-block;">Accept Invitation</a>
                                    </div>
                                    
                                    <p style="font-size: 14px; color: #94a3b8; text-align: center;">If the button above doesn't work, copy this link:<br/>${setupLink}</p>
                                </div>
                                
                                <div style="background-color: #f8fafc; padding: 20px; text-align: center; border-top: 1px solid #e2e8f0;">
                                  <p style="color: #94a3b8; font-size: 12px; margin: 0;">&copy; ${new Date().getFullYear()} Dads Matter Creatives.</p>
                                </div>
                            </div>
                        </div>
                    `
                }
            });

            setInviteStatus('Waiting for email delivery confirmation...');
            
            // LISTEN for success/failure
            const unsubscribe = onSnapshot(mailRef, (snap) => {
                const data = snap.data();
                if (data?.delivery?.state === 'SUCCESS') {
                    setInviteSuccess(`Invitation sent successfully to ${newUserEmail}`);
                    setInviteStatus('');
                    setNewUserName('');
                    setNewUserEmail('');
                    setManualInviteLink(''); // Clear manual link if email worked
                    fetchTeam();
                    unsubscribe();
                    setIsAddingUser(false);
                    setTimeout(() => {
                        setShowAddUserModal(false);
                        setInviteSuccess('');
                    }, 2000);
                } else if (data?.delivery?.state === 'ERROR') {
                    console.error("Email delivery failed:", data.delivery.error);
                    let errorMsg = `Email failed: ${data.delivery.error}`;
                    if (String(data.delivery.error).includes('535') || String(data.delivery.error).includes('Auth')) {
                        errorMsg = "Email blocked by 20i Server (Auth Error).";
                    }
                    setAddUserError(errorMsg);
                    setInviteStatus('');
                    // Do NOT clear manualInviteLink here, so user can see it
                    fetchTeam(); // User was created in Firestore, so refresh list
                    unsubscribe();
                    setIsAddingUser(false);
                }
            });

        } catch (emailErr: any) {
            console.error("Email sending failed:", emailErr);
            setAddUserError("User created, but email failed to trigger.");
            setIsAddingUser(false);
        }

        // 4. Cleanup Secondary Auth
        await signOut(secondaryAuth);
        await deleteApp(secondaryApp);
        secondaryApp = null; 

    } catch (err: any) {
        console.error("Add user error:", err);
        setAddUserError(err.message || "Error creating user");
        setIsAddingUser(false);
        setInviteStatus('');
    } finally {
        if (secondaryApp) {
             try { await deleteApp(secondaryApp); } catch(e) {}
        }
    }
  };

  const copyManualLink = () => {
      navigator.clipboard.writeText(manualInviteLink);
      alert("Link copied to clipboard!");
  };

  if (loading) return <Layout><div className="h-full flex items-center justify-center text-slate-400">Loading data...</div></Layout>;

  return (
    <Layout>
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 animate-slide-up">
        <div>
            <h2 className="text-3xl font-heading font-bold text-slate-900">Group Members</h2>
            <p className="text-slate-500">Group: {userProfile?.schoolName}</p>
        </div>
        <div className="flex gap-2">
            <Button onClick={() => setShowAddUserModal(true)} variant="outline">+ Invite New Member</Button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl flex items-center gap-3 animate-fade-in">
            <span className="text-xl">⚠️</span> {error}
        </div>
      )}

      <Card className="overflow-hidden shadow-lg border-0 animate-slide-up delay-200">
        <div className="overflow-x-auto min-h-[400px]">
            <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-slate-50/80 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                <th className="px-6 py-5 font-bold">Name</th>
                {COURSES.map(c => (
                    <th key={c.id} className="px-4 py-5 text-center cursor-help group/th" title={c.title}>
                        <div className="flex flex-col items-center">
                            <span className="text-xl mb-1 grayscale group-hover/th:grayscale-0 transition-all">{c.icon}</span>
                        </div>
                    </th>
                ))}
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
                {team.map((user) => (
                <tr key={user.uid} className="hover:bg-slate-50/80 transition-colors group">
                    <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-600 transition-colors">
                                {user.displayName.charAt(0)}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <button 
                                        onClick={() => setManagingUser(user)}
                                        className="font-bold text-slate-700 hover:text-indigo-600 text-left block"
                                    >
                                        {user.displayName}
                                    </button>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); setUserToDelete(user); }}
                                        className="text-slate-300 hover:text-rose-500 transition-colors"
                                        title="Remove Member"
                                    >
                                        <span className="text-sm">🗑️</span>
                                    </button>
                                </div>
                                <p className="text-xs text-slate-400">{user.email}</p>
                            </div>
                        </div>
                    </td>
                    {COURSES.map(c => {
                        const prog = user.progress?.[c.id];
                        let status = <div className="w-2 h-2 rounded-full bg-slate-200 mx-auto" title="Not Started"></div>;
                        let dateDisplay = null;
                        
                        if (prog?.quizPassed) {
                            status = <span className="text-emerald-500 text-lg">●</span>;
                            const dateRaw = prog.dateCompleted || prog.lastAccessed;
                            const dateStr = formatDateShort(dateRaw);
                            if (dateStr) {
                                dateDisplay = <div className="text-xs text-slate-600 font-medium mt-1">{dateStr}</div>;
                            }
                        } else if (prog) {
                            const pct = Math.round((prog.completedModules.length / c.totalModules) * 100);
                            status = <span className="text-xs font-bold text-amber-500">{pct}%</span>;
                        }

                        return (
                            <td key={c.id} className="px-4 py-4 text-center border-l border-slate-50">
                                {status}
                                {dateDisplay}
                            </td>
                        );
                    })}
                </tr>
                ))}
            </tbody>
            </table>
        </div>
      </Card>

      {userToDelete && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
             <Card className="w-full max-w-lg p-8 shadow-2xl">
                 <div className="text-center mb-6">
                     <h3 className="text-xl font-heading font-bold text-slate-900">Remove Member?</h3>
                     <p className="text-slate-500 mt-2">Are you sure you want to remove <strong>{userToDelete.displayName}</strong> from the group?</p>
                 </div>
                 <div className="flex gap-3">
                     <Button variant="ghost" onClick={() => setUserToDelete(null)} className="flex-1">Cancel</Button>
                     <Button variant="danger" onClick={handleDeleteUser} disabled={isDeleting} className="flex-1">
                         {isDeleting ? "Removing..." : "Remove"}
                     </Button>
                 </div>
             </Card>
        </div>
      )}

      {managingUser && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
            <Card className="w-full max-w-2xl p-8 shadow-2xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-start mb-6 border-b border-slate-100 pb-4">
                    <div>
                        <h3 className="text-xl font-heading font-bold text-slate-900">Manage Member Progress</h3>
                        <p className="text-slate-500 font-medium">{managingUser.displayName}</p>
                    </div>
                    <button onClick={() => setManagingUser(null)} className="text-slate-400 hover:text-slate-600 text-2xl">&times;</button>
                </div>
                
                <div className="overflow-y-auto pr-2 space-y-2 flex-1">
                    <div className="divide-y divide-slate-100">
                        {COURSES.map(course => {
                             const prog = managingUser.progress?.[course.id];
                             let statusLabel = <span className="text-slate-400 italic text-xs">Not Started</span>;
                             let canReset = false;

                             if (prog?.quizPassed) {
                                 const dateRaw = prog.dateCompleted || prog.lastAccessed;
                                 const dateStr = formatDateShort(dateRaw);
                                 statusLabel = <Badge color="green">Completed {dateStr ? `- ${dateStr}` : ''}</Badge>;
                                 canReset = true;
                             } else if (prog) {
                                 const pct = Math.round((prog.completedModules.length / course.totalModules) * 100);
                                 statusLabel = <Badge color="yellow">{pct}% Complete</Badge>;
                                 canReset = true;
                             }

                             return (
                                 <div key={course.id} className="py-4 flex items-center justify-between">
                                     <div className="flex items-center gap-4">
                                         <span className="text-2xl w-8 text-center">{course.icon}</span>
                                         <div>
                                             <p className="font-bold text-slate-800 text-sm">{course.title}</p>
                                             <div className="mt-1">{statusLabel}</div>
                                         </div>
                                     </div>
                                     <div>
                                         {canReset && (
                                             <Button 
                                                variant="danger" 
                                                className="!py-1.5 !px-3 text-xs"
                                                onClick={() => handleResetProgress(managingUser, course.id, course.title)}
                                             >
                                                Reset
                                             </Button>
                                         )}
                                     </div>
                                 </div>
                             );
                        })}
                    </div>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 text-right">
                    <Button variant="ghost" onClick={() => setManagingUser(null)}>Close</Button>
                </div>
            </Card>
        </div>
      )}

      {showAddUserModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
            <Card className="w-full max-w-md p-8 shadow-2xl">
                <h3 className="text-xl font-heading font-bold mb-6 text-slate-900">Invite Member</h3>
                
                {addUserError && (
                    <div className="mb-4 p-3 bg-rose-50 text-rose-600 text-sm rounded-lg border border-rose-100">
                        <strong>{addUserError}</strong>
                        {manualInviteLink && (
                            <div className="mt-2 text-slate-700">
                                <p className="mb-1 text-xs text-black font-bold">The user account was created, but email failed.</p>
                                <p className="mb-2 text-xs">Send this link manually:</p>
                                <div className="flex gap-2">
                                    <input 
                                        type="text" 
                                        readOnly 
                                        value={manualInviteLink} 
                                        className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-xs"
                                    />
                                    <button onClick={copyManualLink} className="bg-slate-200 px-3 py-1 rounded text-xs font-bold hover:bg-slate-300">Copy</button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {inviteSuccess && (
                     <div className="mb-4 p-3 bg-emerald-50 text-emerald-600 text-sm rounded-lg border border-emerald-100 flex items-center gap-2">
                        <span>✅</span> {inviteSuccess}
                    </div>
                )}

                {inviteStatus && !inviteSuccess && !addUserError && (
                    <div className="mb-4 p-3 bg-indigo-50 text-indigo-600 text-sm rounded-lg border border-indigo-100 flex items-center gap-2">
                        <span className="animate-spin">⏳</span> {inviteStatus}
                    </div>
                )}
                
                <form onSubmit={handleAddUser} className="space-y-4">
                    <Input 
                        label="Full Name" 
                        value={newUserName} 
                        onChange={e => setNewUserName(e.target.value)} 
                        required 
                    />
                    <Input 
                        label="Email Address" 
                        type="email"
                        value={newUserEmail} 
                        onChange={e => setNewUserEmail(e.target.value)} 
                        required 
                    />

                    <div className="flex gap-3 mt-8 pt-4 border-t border-slate-100">
                        <Button type="button" variant="ghost" onClick={() => setShowAddUserModal(false)} className="flex-1">
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isAddingUser} className="flex-1">
                            {isAddingUser ? "Processing..." : "Send Invitation"}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
      )}

    </Layout>
  );
};

export default ManagerDashboard;
